/**
 * Cumple — Admin backoffice de billing (S4-E).
 *
 * Lista de payments (default status=pending) con filtros por status y
 * provider. Click en una row → detalle.
 *
 * Solo accesible para usuarios role=admin (gated por RequireFullAuth +
 * el backend verifica el role; el front solo ofusca).
 */
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import { ApiError } from '../api/client.js';
import {
  adminBillingApi,
  type PaymentListItem,
  type PaymentProvider,
  type PaymentStatus,
} from '../api/admin-billing.js';

const STATUS_LABELS: Record<PaymentStatus, string> = {
  pending: 'Pendiente',
  paid: 'Pagado',
  failed: 'Fallido',
  refunded: 'Reembolsado',
  disputed: 'Disputado',
};

const STATUS_COLORS: Record<PaymentStatus, string> = {
  pending: 'bg-amber-100 text-amber-800',
  paid: 'bg-emerald-100 text-emerald-800',
  failed: 'bg-red-100 text-red-800',
  refunded: 'bg-slate-100 text-slate-700',
  disputed: 'bg-purple-100 text-purple-800',
};

export function AdminBillingPage(): JSX.Element {
  const [payments, setPayments] = useState<PaymentListItem[]>([]);
  const [total, setTotal] = useState(0);
  const [status, setStatus] = useState<PaymentStatus | ''>('pending');
  const [provider, setProvider] = useState<PaymentProvider | ''>('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setLoading(true);
    setError(null);
    adminBillingApi
      .listPayments({
        ...(status ? { status } : {}),
        ...(provider ? { provider } : {}),
      })
      .then((r) => {
        setPayments(r.payments);
        setTotal(r.total);
      })
      .catch((err: unknown) => {
        if (err instanceof ApiError) setError(`Error ${err.status}: ${err.code}`);
        else setError('Error de red');
      })
      .finally(() => setLoading(false));
  }, [status, provider]);

  return (
    <main className="mx-auto max-w-6xl px-6 py-8">
      <header className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-brand-900">Cobranza manual</h1>
          <p className="text-sm text-slate-500">
            Payments pendientes + historial. Mark-paid registra evento append-only en audit log.
          </p>
        </div>
        <Link
          to="/admin/billing/subscriptions"
          className="text-sm text-brand-700 underline hover:no-underline"
        >
          Ver subscriptions →
        </Link>
      </header>

      <div className="mb-4 flex gap-3 items-center text-sm">
        <label>
          Estado:{' '}
          <select
            value={status}
            onChange={(e) => setStatus(e.target.value as PaymentStatus | '')}
            className="rounded border-slate-300 px-2 py-1"
          >
            <option value="">Todos</option>
            <option value="pending">Pendientes</option>
            <option value="paid">Pagados</option>
            <option value="failed">Fallidos</option>
            <option value="refunded">Reembolsados</option>
            <option value="disputed">Disputados</option>
          </select>
        </label>
        <label>
          Provider:{' '}
          <select
            value={provider}
            onChange={(e) => setProvider(e.target.value as PaymentProvider | '')}
            className="rounded border-slate-300 px-2 py-1"
          >
            <option value="">Todos</option>
            <option value="paypal">PayPal</option>
            <option value="azul">Azul</option>
            <option value="cardnet">CardNet</option>
            <option value="transfer">Transferencia</option>
            <option value="manual">Manual</option>
          </select>
        </label>
        <span className="text-slate-500 ml-auto">{total} resultados</span>
      </div>

      {error && (
        <div role="alert" className="mb-4 rounded bg-red-50 px-3 py-2 text-sm text-red-700">
          {error}
        </div>
      )}

      {loading && <p className="text-sm text-slate-500">Cargando…</p>}

      {!loading && payments.length === 0 && !error && (
        <p className="text-sm text-slate-500 text-center py-8">No hay payments con esos filtros.</p>
      )}

      {!loading && payments.length > 0 && (
        <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-left text-xs uppercase text-slate-500">
              <tr>
                <th className="px-4 py-2">Tenant</th>
                <th className="px-4 py-2">Monto</th>
                <th className="px-4 py-2">Provider</th>
                <th className="px-4 py-2">Estado</th>
                <th className="px-4 py-2">Vencimiento</th>
                <th className="px-4 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {payments.map((p) => (
                <tr key={p.id} className="border-t border-slate-100 hover:bg-slate-50">
                  <td className="px-4 py-2 font-medium">{p.tenant_name}</td>
                  <td className="px-4 py-2">
                    {p.currency} {p.amount.toFixed(2)}
                  </td>
                  <td className="px-4 py-2 text-slate-600">{p.provider}</td>
                  <td className="px-4 py-2">
                    <span
                      className={`inline-block rounded px-2 py-0.5 text-xs ${STATUS_COLORS[p.status]}`}
                    >
                      {STATUS_LABELS[p.status]}
                    </span>
                  </td>
                  <td className="px-4 py-2 text-slate-600">
                    {new Date(p.due_at).toLocaleDateString('es-DO')}
                  </td>
                  <td className="px-4 py-2 text-right">
                    <Link
                      to={`/admin/billing/payments/${p.id}`}
                      className="text-brand-700 underline hover:no-underline"
                    >
                      Ver
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </main>
  );
}
